import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  FaArrowRight,
  FaEnvelope,
  FaHome,
  FaKey,
  FaLifeRing,
  FaLock,
  FaRocket,
  FaShieldAlt,
  FaSignOutAlt,
  FaStar,
} from "react-icons/fa";
import { toast } from "react-toastify";
import StudentSidebar from "../components/StudentSidebar";
import { terminateSession } from "../utils/sessionManager";
import { revokeBrowserSession } from "../api/authSession";
import { requestCancellation } from "../api/payments";
import StudentProfilePage from "../components/StudentProfilePage";
import TeacherProfilePage from "../components/TeacherProfilePage";
import ManagementProfilePage from "../components/ManagementProfilePage";
import PasswordResetModal from "../components/PasswordResetModal";
import { useStudentProfile } from "../context/StudentProfileContext";
import { useTeacherProfile } from "../context/TeacherProfileContext";
import { useTenant } from "../context/TenantContext";
import {
  ADMIN_ROLES,
  getDashboardPathForRole,
  normalizeRole,
  ROLE_SUPER_ADMIN,
  ROLE_TEACHER,
  STUDENT_ROLES,
} from "../utils/authRole";

const formatDateLabel = (value) => {
  if (!value || value === "N/A") return "N/A";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  return date.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const formatCurrencyLabel = (value) => {
  const numericValue = Number(value ?? 0);

  if (Number.isNaN(numericValue)) return "INR 0";

  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(numericValue);
};

const formatRoleLabel = (role) => {
  if (!role) return "User";

  return role
    .replace(/^ROLE_/, "")
    .toLowerCase()
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

const PREMIUM_PLAN_SET = new Set(["PREMIUM", "GROWTH", "ENTERPRISE", "PRO"]);
const FREE_TRIAL_PLAN_SET = new Set(["FREE", "TRIAL", "FREE_TRIAL", "STARTER", "BASIC"]);

const getPlanBadge = (planValue) => {
  const normalizedPlan = String(planValue || "").trim().toUpperCase();

  if (PREMIUM_PLAN_SET.has(normalizedPlan)) {
    return {
      label: "Premium",
      className: "border-amber-200 bg-amber-50 text-amber-700",
    };
  }

  if (FREE_TRIAL_PLAN_SET.has(normalizedPlan)) {
    return {
      label: "Free Trial",
      className: "border-sky-200 bg-sky-50 text-sky-700",
    };
  }

  return null;
};

export default function Profile({ embedded = false }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [showResetForm, setShowResetForm] = useState(false);
  const { studentProfile, studentProfileLoading } = useStudentProfile();
  const { teacherProfile, teacherProfileLoading } = useTeacherProfile();
  const { activeTenant } = useTenant();

  const storedRole = normalizeRole(localStorage.getItem("user"));
  const isStudentRole = STUDENT_ROLES.includes(storedRole);
  const isTeacherRole = storedRole === ROLE_TEACHER;
  const isAdminRole = ADMIN_ROLES.includes(storedRole);
  const isSuperAdminRole = storedRole === ROLE_SUPER_ADMIN;
  const isTuitionTenant = String(activeTenant?.tenantType || activeTenant?.type || "").toUpperCase() === "TUITION";
  const dashboardPath = isTuitionTenant
    ? (isTeacherRole ? "/tuition/teacher" : isStudentRole ? "/tuition/student" : "/tuition/dashboard")
    : getDashboardPathForRole(storedRole);
  const storedName =
    localStorage.getItem("currentUserName") ||
    localStorage.getItem("username") ||
    "ClassNX User";
  const displayName = useMemo(() => {
    return storedName
      .split("@")[0]
      .replace(/[._-]+/g, " ")
      .replace(/\b\w/g, (char) => char.toUpperCase());
  }, [storedName]);
  const initials = useMemo(() => {
    return displayName
      .split(" ")
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0])
      .join("")
      .toUpperCase();
  }, [displayName]);

  useEffect(() => {
    const prefillEmail = location.state?.email;
    const infoMessage = location.state?.message;

    if (prefillEmail) {
      setEmail(prefillEmail);
    }

    if (infoMessage) {
      toast.info(infoMessage, {
        position: "top-center",
      });
      setShowResetForm(true);
    }
  }, [location.state]);

  useEffect(() => {
    if (!location.state?.email) {
      setEmail((current) => current || localStorage.getItem("authEmail") || "");
    }
  }, [location.state]);

  const handleResetPassword = async (event) => {
    event.preventDefault();
    toast.info("Contact your administrator to request a secure password reset.", { position: "top-center" });
    setShowResetForm(false);
  };

  const handleLogout = async () => {
    if (isLoggingOut) return;
    setIsLoggingOut(true);
    window.dispatchEvent(new CustomEvent("auth:logout-progress", { detail: { pending: true } }));
    try {
      await revokeBrowserSession();
      terminateSession({ reason: "manual", redirect: false, notify: false });
      window.location.assign("/login");
    } catch (error) {
      // Do not show the login form while the backend cookie is still valid.
      toast.error(error?.message || "Logout failed. Your session is still active; please try again.");
      setIsLoggingOut(false);
      window.dispatchEvent(new CustomEvent("auth:logout-progress", { detail: { pending: false } }));
    }
  };

  const handleCopyEmail = async () => {
    if (!email) return;

    try {
      await navigator.clipboard.writeText(email);
      toast.success("Email copied.", { position: "top-center" });
    } catch {
      toast.error("Unable to copy email.", { position: "top-center" });
    }
  };

  const handleRequestSubscriptionCancellation = async () => {
    if (!isAdminRole && !isSuperAdminRole) {
      toast.error("Only Admin or Super Admin can request cancellation.", {
        position: "top-center",
      });
      return;
    }

    const shouldSubmit = window.confirm("Submit subscription cancellation request to super admin?");
    if (!shouldSubmit) return;

    const reasonInput = window.prompt(
      "Reason for cancellation (optional):",
      "Need to discontinue subscription"
    );
    const reason = (reasonInput || "Need to discontinue subscription").trim() || "Need to discontinue subscription";

    try {
      await requestCancellation({ reason });
      toast.success("Cancellation request submitted. The platform team will review it shortly.", {
        position: "top-center",
      });
    } catch (error) {
      const message =
        error?.response?.data?.message
        || error?.response?.data?.error
        || error?.message
        || "Could not submit cancellation request.";
      toast.error(message, {
        position: "top-center",
      });
    }
  };

  const accountActions = [
    {
      title: "Open Dashboard",
      description: "Return to your main dashboard and continue daily work.",
      icon: FaHome,
      href: dashboardPath,
      kind: "link",
    },
    {
      title: "Reset Password",
      description: "Update your password from this profile screen.",
      icon: FaKey,
      kind: "button",
      onClick: () => setShowResetForm((current) => !current),
    },
    {
      title: "Contact Support",
      description: "Reach the ClassNX support desk for account help.",
      icon: FaLifeRing,
      href: "mailto:support@classnx.com",
      kind: "external",
    },
  ];
  const studentInfo = studentProfile?.studentInfo || {};
  const studentSchoolName = studentProfile?.schoolName || activeTenant?.name || "ClassNX";
  const studentPaymentStatus = studentProfile?.paymentStatus || "PENDING";
  const studentTotalPaid = formatCurrencyLabel(studentProfile?.totalPaid ?? 0);
  const studentNextPaymentDate = formatDateLabel(studentProfile?.nextPaymentDate || "N/A");
  const teacherName = teacherProfile?.name || displayName;
  const teacherSchoolName = teacherProfile?.schoolName || activeTenant?.name || "ClassNX";
  const teacherSubject = teacherProfile?.subject || "Not assigned";
  const adminOrganizationName = localStorage.getItem("activeTenantName") || "ClassNX";
  const subscriptionPlan =
    activeTenant?.subscription?.plan
    || "FREE";
  const subscriptionStartRaw =
    activeTenant?.subscription?.raw?.startDate
    || activeTenant?.subscription?.raw?.planStartDate
    || activeTenant?.subscription?.raw?.plan_start_date
    || activeTenant?.subscription?.raw?.subscriptionStartDate
    || activeTenant?.subscription?.raw?.subscription_start_date
    || "";
  const subscriptionExpiryRaw =
    activeTenant?.subscription?.renewalDate
    || activeTenant?.subscription?.raw?.expiryDate
    || activeTenant?.subscription?.raw?.planExpiryDate
    || activeTenant?.subscription?.raw?.plan_expiry_date
    || activeTenant?.subscription?.raw?.expiresOn
    || "";
  const subscriptionDuration = (() => {
    const start = new Date(subscriptionStartRaw);
    const end = new Date(subscriptionExpiryRaw);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end < start) {
      return "N/A";
    }
    const diffDays = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
    if (diffDays >= 365 && diffDays % 365 === 0) {
      const years = diffDays / 365;
      return `${years} year${years > 1 ? "s" : ""}`;
    }
    if (diffDays >= 30 && diffDays % 30 === 0) {
      const months = diffDays / 30;
      return `${months} month${months > 1 ? "s" : ""}`;
    }
    return `${diffDays} day${diffDays > 1 ? "s" : ""}`;
  })();
  const subscriptionInfo = {
    plan: subscriptionPlan,
    startDate: formatDateLabel(subscriptionStartRaw || "N/A"),
    expiryDate: formatDateLabel(subscriptionExpiryRaw || "N/A"),
    duration: subscriptionDuration,
  };
  const planBadge = getPlanBadge(subscriptionInfo.plan);

  const managementQuickActions = isTuitionTenant
    ? [
        {
          title: "Open Tuition Dashboard",
          description: "Return to your tuition workspace.",
          kind: "link",
          href: dashboardPath,
          icon: FaHome,
        },
        {
          title: "Reset Password",
          description: "Open the security form and update your account credentials.",
          kind: "button",
          onClick: () => setShowResetForm((current) => !current),
          icon: FaKey,
        },
        {
          title: "Contact Support",
          description: "Get assistance with your tuition workspace.",
          kind: "external",
          href: "mailto:support@classnx.com",
          icon: FaLifeRing,
        },
      ]
    : isSuperAdminRole
    ? [
        {
          title: "Open Super Admin",
          description: "Manage tenant onboarding, subscriptions, and platform operations.",
          kind: "link",
          href: "/super-admin",
          icon: FaHome,
        },
        {
          title: "Open Admin Board",
          description: "Jump into the central dashboard and school operations.",
          kind: "link",
          href: "/admin-board",
          icon: FaArrowRight,
        },
        {
          title: "Reset Password",
          description: "Open the security form and update your account credentials.",
          kind: "button",
          onClick: () => setShowResetForm((current) => !current),
          icon: FaKey,
        },
        {
          title: "Request Subscription Cancellation",
          description: "Submit cancellation request for your current subscription.",
          kind: "button",
          onClick: handleRequestSubscriptionCancellation,
          icon: FaSignOutAlt,
        },
        {
          title: "Contact Support",
          description: "Get assistance with tenant controls or account access.",
          kind: "external",
          href: "mailto:support@classnx.com",
          icon: FaLifeRing,
        },
      ]
    : [
        {
          title: "Open Admin Board",
          description: "Return to your admin dashboard and daily control center.",
          kind: "link",
          href: "/admin-board",
          icon: FaHome,
        },
        {
          title: "Manage Students",
          description: "Open student records, class placement, and fee status tools.",
          kind: "link",
          href: "/admin/students",
          icon: FaArrowRight,
        },
        {
          title: "Reset Password",
          description: "Open the security form and update your account credentials.",
          kind: "button",
          onClick: () => setShowResetForm((current) => !current),
          icon: FaKey,
        },
        {
          title: "Request Subscription Cancellation",
          description: "Submit cancellation request for your current subscription.",
          kind: "button",
          onClick: handleRequestSubscriptionCancellation,
          icon: FaSignOutAlt,
        },
        {
          title: "Contact Support",
          description: "Get assistance with account access and admin controls.",
          kind: "external",
          href: "mailto:support@classnx.com",
          icon: FaLifeRing,
        },
      ];

  if (isTeacherRole) {
    return (
      <TeacherProfilePage
        displayName={displayName}
        initials={initials}
        email={teacherProfile?.email || email}
        roleLabel={formatRoleLabel(storedRole)}
        dashboardPath={dashboardPath}
        teacherName={teacherName}
        teacherSchoolName={teacherSchoolName}
        teacherSubject={teacherSubject}
        teacherProfileLoading={teacherProfileLoading}
        subscriptionInfo={subscriptionInfo}
        showResetForm={showResetForm}
        setShowResetForm={setShowResetForm}
        newPassword={newPassword}
        setNewPassword={setNewPassword}
        isSubmitting={isSubmitting}
        handleResetPassword={handleResetPassword}
        handleCopyEmail={handleCopyEmail}
        handleLogout={handleLogout}
      />
    );
  }

  if (isStudentRole) {
    return (
      <StudentProfilePage
        displayName={displayName}
        initials={initials}
        email={email}
        roleLabel={formatRoleLabel(storedRole)}
        dashboardPath={dashboardPath}
        studentProfileLoading={studentProfileLoading}
        studentSchoolName={studentSchoolName}
        studentClassName={studentInfo.studentClass || "N/A"}
        studentRollNumber={studentInfo.rollNo || "N/A"}
        studentPaymentStatus={studentPaymentStatus}
        studentName={studentInfo.name || displayName}
        studentAdmissionDate={formatDateLabel(studentInfo.admissionDate)}
        studentTotalPaid={studentTotalPaid}
        studentNextPaymentDate={studentNextPaymentDate}
        subscriptionInfo={subscriptionInfo}
        showResetForm={showResetForm}
        setShowResetForm={setShowResetForm}
        newPassword={newPassword}
        setNewPassword={setNewPassword}
        isSubmitting={isSubmitting}
        handleResetPassword={handleResetPassword}
        handleCopyEmail={handleCopyEmail}
        handleLogout={handleLogout}
      />
    );
  }

  if (isAdminRole) {
    return (
      <ManagementProfilePage
        displayName={displayName}
        initials={initials}
        email={email}
        roleLabel={formatRoleLabel(storedRole)}
        dashboardPath={dashboardPath}
        primaryName={displayName}
        organizationName={adminOrganizationName}
        roleFocus={isSuperAdminRole ? "Tenant platform governance" : "School operations management"}
        profileLoading={false}
        accountDetails={[
          { label: "Workspace", value: isSuperAdminRole ? "Platform" : "School" },
          { label: "Access Level", value: isSuperAdminRole ? "Global controls" : "Module controls" },
          { label: "Security", value: "Password protected" },
          ...(!isSuperAdminRole ? [
            {
              label: "Subscription Plan",
              value: (
                <span className="inline-flex flex-wrap items-center gap-2">
                  <span>{subscriptionInfo.plan}</span>
                  {planBadge && (
                    <span
                      className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-semibold uppercase tracking-wide ${planBadge.className}`}
                    >
                      {planBadge.label === "Premium" ? <FaStar className="mr-1" /> : <FaRocket className="mr-1" />}
                      {planBadge.label} Badge
                    </span>
                  )}
                </span>
              ),
            },
            { label: "Subscription Start", value: subscriptionInfo.startDate },
            { label: "Subscription Expiry", value: subscriptionInfo.expiryDate },
            { label: "Subscription Duration", value: subscriptionInfo.duration },
          ] : []),
        ]}
        quickActions={managementQuickActions}
        showResetForm={showResetForm}
        setShowResetForm={setShowResetForm}
        newPassword={newPassword}
        setNewPassword={setNewPassword}
        isSubmitting={isSubmitting}
        handleResetPassword={handleResetPassword}
        handleLogout={handleLogout}
        isLoggingOut={isLoggingOut}
      />
    );
  }

  return (
    <div className={embedded ? "bg-slate-50" : "bg-slate-50 px-4 py-8 md:px-6 md:py-10"}>
      <div className={embedded ? "space-y-6" : "mx-auto max-w-6xl space-y-6"}>
        <section className="overflow-hidden rounded-3xl bg-gradient-to-br from-slate-900 via-slate-800 to-indigo-900 text-white shadow-2xl shadow-slate-300/40">
          <div className="grid gap-6 px-6 py-8 md:grid-cols-[1.2fr_0.8fr] md:px-8">
            <div className="flex gap-4">
              <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded-3xl bg-white/10 text-2xl font-bold backdrop-blur-sm">
                {initials || "CU"}
              </div>
              <div>
                <p className="text-sm font-semibold uppercase tracking-[0.22em] text-indigo-200">
                  Account Profile
                </p>
                <h1 className="mt-2 text-3xl font-extrabold md:text-4xl">{displayName}</h1>
                <p className="mt-2 max-w-2xl text-sm text-slate-300 md:text-base">
                  Review your account identity, school record, and security controls from one clean workspace.
                </p>
                <div className="mt-5 flex flex-wrap gap-3 text-sm">
                  <span className="rounded-full border border-white/15 bg-white/10 px-4 py-2 font-semibold">
                    {formatRoleLabel(storedRole)}
                  </span>
                  <span className="rounded-full border border-emerald-300/20 bg-emerald-400/10 px-4 py-2 font-semibold text-emerald-100">
                    Account active
                  </span>
                </div>
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-1 lg:grid-cols-2">
              <div className="rounded-2xl border border-white/10 bg-white/10 p-4 backdrop-blur-sm">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-300">Primary Email</p>
                <div className="mt-3 flex items-start gap-3">
                  <FaEnvelope className="mt-1 text-slate-300" />
                  <div>
                    <p className="font-semibold text-white">{email || "No email available"}</p>
                    <button
                      type="button"
                      onClick={handleCopyEmail}
                      className="mt-2 text-sm font-semibold text-indigo-200 transition hover:text-white"
                    >
                      Copy email
                    </button>
                  </div>
                </div>
              </div>
              <div className="rounded-2xl border border-white/10 bg-white/10 p-4 backdrop-blur-sm">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-300">Account Security</p>
                <div className="mt-3 flex items-start gap-3">
                  <FaShieldAlt className="mt-1 text-slate-300" />
                  <div>
                    <p className="font-semibold text-white">Password protection enabled</p>
                    <p className="mt-1 text-sm text-slate-300">Change your password below whenever you need to secure access again.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="grid gap-6 lg:grid-cols-[1.25fr_0.75fr]">
          <div className="space-y-6">
            <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-lg shadow-slate-200/50">
              <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Account Summary</p>
                  <h2 className="mt-2 text-2xl font-bold text-slate-900">Core account details</h2>
                </div>
                <Link
                  to={dashboardPath}
                  className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:border-slate-300 hover:bg-slate-50"
                >
                  Open Dashboard <FaArrowRight className="text-xs" />
                </Link>
              </div>

              <div className="mt-6 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                <div className="rounded-2xl bg-slate-50 p-4">
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Account Name</p>
                  <p className="mt-2 text-lg font-bold text-slate-900">{displayName}</p>
                </div>
                <div className="rounded-2xl bg-slate-50 p-4">
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Access Level</p>
                  <p className="mt-2 text-lg font-bold text-slate-900">{formatRoleLabel(storedRole)}</p>
                </div>
                <div className="rounded-2xl bg-slate-50 p-4">
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Login Email</p>
                  <p className="mt-2 text-sm font-bold text-slate-900 break-all">{email || "Not available"}</p>
                </div>
                <div className="rounded-2xl bg-slate-50 p-4">
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Account Status</p>
                  <p className="mt-2 text-lg font-bold text-emerald-600">Active</p>
                </div>
              </div>
            </div>

            {isStudentRole && (
              <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-lg shadow-slate-200/50">
                <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Student Record</p>
                    <h2 className="mt-2 text-2xl font-bold text-slate-900">School, class, and fee status</h2>
                  </div>
                  <div className="rounded-full border border-slate-200 bg-slate-50 px-4 py-2 text-sm font-semibold text-slate-700">
                    {studentProfileLoading ? "Syncing school record..." : "Student record ready"}
                  </div>
                </div>

                <div className="mt-6 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                  <div className="rounded-2xl bg-slate-50 p-4">
                    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">School Name</p>
                    <p className="mt-2 text-lg font-bold text-slate-900">{studentSchoolName}</p>
                  </div>
                  <div className="rounded-2xl bg-slate-50 p-4">
                    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Current Class</p>
                    <p className="mt-2 text-lg font-bold text-slate-900">{studentInfo.studentClass || "N/A"}</p>
                  </div>
                  <div className="rounded-2xl bg-slate-50 p-4">
                    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Roll Number</p>
                    <p className="mt-2 text-lg font-bold text-slate-900">{studentInfo.rollNo || "N/A"}</p>
                  </div>
                  <div className="rounded-2xl bg-slate-50 p-4">
                    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Fee Status</p>
                    <p className="mt-2 text-lg font-bold text-slate-900">{studentPaymentStatus}</p>
                  </div>
                </div>

                <div className="mt-4 grid gap-4 md:grid-cols-3">
                  <div className="rounded-2xl border border-slate-200 bg-white p-4">
                    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Registered Student Name</p>
                    <p className="mt-2 text-base font-bold text-slate-900">{studentInfo.name || displayName}</p>
                  </div>
                  <div className="rounded-2xl border border-slate-200 bg-white p-4">
                    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Admission Date</p>
                    <p className="mt-2 text-base font-bold text-slate-900">{formatDateLabel(studentInfo.admissionDate)}</p>
                  </div>
                  <div className="rounded-2xl border border-slate-200 bg-white p-4">
                    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Fees Paid So Far</p>
                    <p className="mt-2 text-base font-bold text-slate-900">{studentTotalPaid}</p>
                    <p className="mt-1 text-sm text-slate-500">Next due date: {studentNextPaymentDate}</p>
                  </div>
                </div>
              </div>
            )}

            <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-lg shadow-slate-200/50">
              <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Security Settings</p>
                  <h2 className="mt-2 text-2xl font-bold text-slate-900">Password and access control</h2>
                </div>
                <button
                  type="button"
                  onClick={() => setShowResetForm((current) => !current)}
                  className="inline-flex items-center gap-2 rounded-full bg-slate-900 px-4 py-2 text-sm font-semibold text-white transition hover:bg-slate-800"
                >
                  <FaLock className="text-xs" /> {showResetForm ? "Hide Password Form" : "Change Password"}
                </button>
              </div>

              <div className="mt-5 grid gap-4 md:grid-cols-3">
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <p className="text-sm font-semibold text-slate-900">Strong password</p>
                  <p className="mt-2 text-sm text-slate-600">Use a unique password with a mix of letters, numbers, and symbols.</p>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <p className="text-sm font-semibold text-slate-900">Sign-in reset</p>
                  <p className="mt-2 text-sm text-slate-600">After you change the password, you will sign in again with the new one.</p>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <p className="text-sm font-semibold text-slate-900">Support help</p>
                  <p className="mt-2 text-sm text-slate-600">If access fails, contact support with your school name and login email.</p>
                </div>
              </div>

              <div className="mt-6 rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-5 py-6 text-sm leading-6 text-slate-600">
                Password reset now opens in a modal dialog so the profile page stays focused on account and guidance details.
              </div>
            </div>
          </div>

          <div className="space-y-6">
            <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-lg shadow-slate-200/50">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Account Tools</p>
              <h2 className="mt-2 text-2xl font-bold text-slate-900">Useful shortcuts</h2>
              <div className="mt-5 space-y-3">
                {accountActions.map((action) => {
                  const Icon = action.icon;

                  if (action.kind === "link") {
                    return (
                      <Link
                        key={action.title}
                        to={action.href}
                        className="flex items-start gap-4 rounded-2xl border border-slate-200 p-4 transition hover:border-slate-300 hover:bg-slate-50"
                      >
                        <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-slate-100 text-slate-700">
                          <Icon />
                        </div>
                        <div>
                          <p className="font-semibold text-slate-900">{action.title}</p>
                          <p className="mt-1 text-sm text-slate-500">{action.description}</p>
                        </div>
                      </Link>
                    );
                  }

                  if (action.kind === "external") {
                    return (
                      <a
                        key={action.title}
                        href={action.href}
                        className="flex items-start gap-4 rounded-2xl border border-slate-200 p-4 transition hover:border-slate-300 hover:bg-slate-50"
                      >
                        <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-slate-100 text-slate-700">
                          <Icon />
                        </div>
                        <div>
                          <p className="font-semibold text-slate-900">{action.title}</p>
                          <p className="mt-1 text-sm text-slate-500">{action.description}</p>
                        </div>
                      </a>
                    );
                  }

                  return (
                    <button
                      type="button"
                      key={action.title}
                      onClick={action.onClick}
                      className="flex w-full items-start gap-4 rounded-2xl border border-slate-200 p-4 text-left transition hover:border-slate-300 hover:bg-slate-50"
                    >
                      <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-slate-100 text-slate-700">
                        <Icon />
                      </div>
                      <div>
                        <p className="font-semibold text-slate-900">{action.title}</p>
                        <p className="mt-1 text-sm text-slate-500">{action.description}</p>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-lg shadow-slate-200/50">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Account Guidance</p>
              <h2 className="mt-2 text-2xl font-bold text-slate-900">Keep this profile accurate</h2>
              <div className="mt-5 space-y-3 text-sm text-slate-600">
                <div className="rounded-2xl bg-slate-50 p-4">Check your email and role details after every account update.</div>
                <div className="rounded-2xl bg-slate-50 p-4">Change your password immediately if you notice unusual access.</div>
                <div className="rounded-2xl bg-slate-50 p-4">Use the dashboard shortcut to return to your daily workflow faster.</div>
              </div>
              <button
                type="button"
                onClick={handleLogout}
                disabled={isLoggingOut}
                className="mt-5 inline-flex items-center gap-2 rounded-full bg-rose-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-rose-700 disabled:cursor-wait disabled:opacity-70"
              >
                {isLoggingOut ? <><span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" aria-hidden="true" /> Logging out...</> : <><FaSignOutAlt className="text-xs" /> Logout</>}
              </button>
            </div>
          </div>
        </section>
      </div>
      <PasswordResetModal
        isOpen={showResetForm}
        title="Reset account password"
        description="Update the password for this account. Use the new credential the next time you sign in."
        email={email}
        newPassword={newPassword}
        setNewPassword={setNewPassword}
        isSubmitting={isSubmitting}
        onClose={() => setShowResetForm(false)}
        onSubmit={handleResetPassword}
      />
    </div>
  );
}
